import type { GeocodeResult, HierarchyNode } from './types';

const streetLabel = (node: HierarchyNode): string => {
  const short = node.short.replace(/\.$/, '').toLowerCase();
  if (['пр-кт', 'просп', 'проспект'].includes(short)) return `${node.name} проспект`;
  const types: Record<string, string> = {
    ул: 'улица', пер: 'переулок', наб: 'набережная', ш: 'шоссе',
    'б-р': 'бульвар', 'пр-д': 'проезд', пл: 'площадь',
  };
  return `${types[short] ?? node.short} ${node.name}`.trim();
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Compact search labels; official fullAddress and the selected object stay intact. */
export function splitDisplay(result: GeocodeResult): { primary: string; secondary: string } {
  const { hierarchy, house } = result;
  if (hierarchy.length === 0) return { primary: result.fullAddress, secondary: '' };
  const count = result.precision === 'house' && hierarchy.length > 1 ? 2 : 1;
  const primaryNodes = hierarchy.slice(-count);
  const ancestors = hierarchy.slice(0, -count);
  const letters: string[] = [];
  let houseName = house?.name ?? primaryNodes.at(-1)!.name;
  if (result.precision === 'house' && house) {
    for (const [type, value] of [[house.addtype1, house.addnum1], [house.addtype2, house.addnum2]]) {
      if (Number(type) !== 4 || !value?.trim() || value.trim() === '-') continue;
      const pattern = new RegExp(`\\s+лит(?:ера)?\\.?\\s+${escapeRegExp(value.trim())}(?=\\s|$)`, 'iu');
      if (!pattern.test(houseName)) continue;
      houseName = houseName.replace(pattern, '').trim();
      letters.push(`литера ${value.trim()}`);
    }
  }
  const primary = primaryNodes.map(node => {
    if (node.level === 10 && result.precision === 'house') return houseName;
    return node.level === 8 || node.level === 9 ? streetLabel(node) : node.name;
  }).join(', ');

  const locality = [...ancestors].reverse().find(node => node.level === 5 || node.level === 6 ||
    (node.level === 4 && /^(г|пгт|п|с)\.?$/iu.test(node.short)));
  const region = ancestors.find(node => node.level === 1);
  const places = [locality?.name, region?.name].filter((name): name is string => !!name);
  const location = [...new Set(places)].join(', ');
  return { primary, secondary: [location, ...letters].filter(Boolean).join(' · ') };
}
